import { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function MQLs() {
  const [mqls, setMQLs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMQL, setSelectedMQL] = useState(null);
  const [showMarkSold, setShowMarkSold] = useState(null);
  const [buyerName, setBuyerName] = useState('');

  useEffect(() => {
    fetchMQLs();
  }, []);

  const fetchMQLs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/mqls`);
      setMQLs(response.data.mqls);
    } catch (error) {
      console.error('Error fetching MQLs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkSold = async (mqlId) => {
    try {
      await axios.put(`${API_BASE}/mqls/${mqlId}/mark-sold`, {
        buyer_name: buyerName
      });
      setShowMarkSold(null);
      setBuyerName('');
      fetchMQLs();
    } catch (error) {
      console.error('Error marking MQL as sold:', error);
      alert('Error marking MQL as sold');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_BASE}/mqls/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `mqls-export-${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting MQLs:', error);
      alert('Error exporting MQLs');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">MQLs</h1>
        <button
          onClick={handleExport}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Export CSV
        </button>
      </div>

      {loading ? (
        <div className="text-center p-8">Loading MQLs...</div>
      ) : mqls.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No MQLs found. MQLs are created when leads convert via landing pages.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Times Sold</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mqls.map((mql) => (
                <tr key={mql.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {mql.first_name} {mql.last_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mql.company_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mql.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      mql.grade === 'A+' ? 'bg-green-100 text-green-800' :
                      mql.grade === 'A' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {mql.grade}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {mql.times_sold || 0}/5
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    ${parseFloat(mql.total_revenue || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {mql.times_sold < 5 ? (
                      <button
                        onClick={() => setShowMarkSold(showMarkSold === mql.id ? null : mql.id)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Mark Sold
                      </button>
                    ) : (
                      <span className="text-gray-400">Maxed Out</span>
                    )}
                    <button
                      onClick={() => setSelectedMQL(selectedMQL === mql.id ? null : mql.id)}
                      className="ml-4 text-gray-600 hover:text-gray-800"
                    >
                      {selectedMQL === mql.id ? 'Hide' : 'View'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mark Sold Form */}
          {showMarkSold && (
            <div className="p-4 bg-gray-50 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Buyer name"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                />
                <button
                  onClick={() => handleMarkSold(showMarkSold)}
                  disabled={!buyerName}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  Confirm Sale
                </button>
                <button
                  onClick={() => {
                    setShowMarkSold(null);
                    setBuyerName('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* MQL Details */}
          {selectedMQL && (
            <div className="p-6 bg-gray-50 border-t">
              {(() => {
                const mql = mqls.find(m => m.id === selectedMQL);
                if (!mql) return null;
                return (
                  <div>
                    <h3 className="font-semibold mb-2">AI Analysis</h3>
                    <p className="text-sm text-gray-600 mb-4">{mql.ai_reasoning}</p>
                    {mql.pain_points && Array.isArray(mql.pain_points) && mql.pain_points.length > 0 && (
                      <div className="mb-4">
                        <h4 className="font-medium mb-1">Pain Points:</h4>
                        <ul className="list-disc list-inside text-sm text-gray-600">
                          {mql.pain_points.map((point, idx) => (
                            <li key={idx}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {mql.recommended_pitch && (
                      <div>
                        <h4 className="font-medium mb-1">Recommended Pitch:</h4>
                        <p className="text-sm text-gray-600">{mql.recommended_pitch}</p>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
